import { filter, map, scan, startWith, switchMap, tap } from 'rxjs/operators'
import { combineLatest, Observable, ReplaySubject, Subject, Subscription } from 'rxjs'
import { equals } from 'ramda'

import { ActivatedRoute } from '@angular/router'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core'

import { Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import { LocalizationService } from '@app-domains/localization/service/localization.service'
import { ProductHistoryService } from '@app-domains/product/services/product-history/product-history.service'
import {
    RelatedProductsResult,
    RelatedProductsService,
    RelatedProductsStrategy,
} from '@app-domains/product/services/related-products/related-products.service'

export enum OtherProductsCategories {
    RELATED = 'related',
    RECENT = 'recent',
    SAME_SERIES = 'same_series',
    SAME_BRAND = 'same_brand',
}

@Component({
    selector: 'app-other-products',
    templateUrl: './other-products.component.html',
    styleUrls: ['./other-products.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class OtherProductsComponent implements OnInit, OnDestroy {
    @Output()
    public loadMore = new EventEmitter<void>()

    @Output()
    public loadCategory = new EventEmitter<OtherProductsCategories>()

    @Input()
    public otherProducts: Product[] = []

    @Input()
    public set product(value: Product) {
        this.productSubject.next(value)
    }

    public Categories = OtherProductsCategories

    public RelatedProductsStrategy = RelatedProductsStrategy

    public related: RelatedProductsResult = { products: [], strategy: RelatedProductsStrategy.none }
    public lastViewed: Product[] = []
    public sameSeries: Product[] = []
    public sameBrand: Product[] = []

    public relatedLimit = 6
    public lastViewedLimit = 6
    public sameSeriesLimit = 6
    public sameBrandLimit = 6

    public loadMoreSubject = new Subject<OtherProductsCategories>()
    public OtherProductsCategories = OtherProductsCategories

    private readonly productSubject = new ReplaySubject<Product>(1)
    private readonly subscriptions = new Subscription()

    constructor(
        private route: ActivatedRoute,
        public productHistoryService: ProductHistoryService,
        private localization: LocalizationService,
        private relatedProductsService: RelatedProductsService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
    }

    public ngOnInit() {
        const locale$ = this.localization.currentLocale$
        const product$ = this.productSubject.asObservable().pipe(
            filter((product): product is Product => !! product?.ean),
        )

        this.subscriptions.add(
            combineLatest([
                this.route.params,
                locale$,
                this.createLimit(OtherProductsCategories.RELATED),
                product$,
            ]).pipe(
                switchMap(([, locale, limit, product]) => this.relatedProductsService.getRelatedProducts(
                    product,
                    locale,
                    limit,
                )),
                tap((related) => {
                    this.related = related
                    this.changeDetectorRef.markForCheck()
                }),
            ).subscribe(),
        )

        this.subscriptions.add(
            combineLatest([
                this.productHistoryService.history$,
                locale$,
                this.createLimit(OtherProductsCategories.RECENT),
                product$,
            ]).pipe(
                switchMap(([eans, locale, limit, product]) => this.relatedProductsService.getLastViewedProducts(
                    eans,
                    product.ean,
                    locale,
                    limit,
                )),
                tap((products) => {
                    this.lastViewed = products
                    this.changeDetectorRef.markForCheck()
                }),
            ).subscribe(),
        )

        this.subscriptions.add(
            combineLatest([
                this.route.params,
                locale$,
                this.createLimit(OtherProductsCategories.SAME_SERIES),
                product$,
            ]).pipe(
                switchMap(([, locale, limit, product]) => this.relatedProductsService.getSameSeriesProducts(
                    product,
                    locale,
                    limit,
                )),
                tap((products) => {
                    this.sameSeries = products
                    this.changeDetectorRef.markForCheck()
                }),
            ).subscribe(),
        )

        this.subscriptions.add(
            combineLatest([
                this.route.params,
                locale$,
                this.createLimit(OtherProductsCategories.SAME_BRAND),
                product$,
            ]).pipe(
                switchMap(([, locale, limit, product]) => this.relatedProductsService.getSameBrandProducts(
                    product,
                    locale,
                    limit,
                )),
                tap((products) => {
                    this.sameBrand = products
                    this.changeDetectorRef.markForCheck()
                }),
            ).subscribe(),
        )

        this.subscriptions.add(
            this.createLimit(OtherProductsCategories.RELATED).subscribe((limit) => {
                this.relatedLimit = limit
            }),
        )

        this.subscriptions.add(
            this.createLimit(OtherProductsCategories.RECENT).subscribe((limit) => {
                this.lastViewedLimit = limit
            }),
        )

        this.subscriptions.add(
            this.createLimit(OtherProductsCategories.SAME_SERIES).subscribe((limit) => {
                this.sameSeriesLimit = limit
            }),
        )

        this.subscriptions.add(
            this.createLimit(OtherProductsCategories.SAME_BRAND).subscribe((limit) => {
                this.sameBrandLimit = limit
            }),
        )
    }

    public ngOnDestroy() {
        this.subscriptions.unsubscribe()
    }

    public loadMoreProducts(type: OtherProductsCategories): void {
        this.loadMoreSubject.next(type)
    }

    private createLimit(type: OtherProductsCategories): Observable<number> {
        return this.loadMoreSubject.pipe(
            startWith(type),
            filter(equals(type)),
            map(() => 6),
            scan((acc, val) => acc + val, 0),
        )
    }

}
